import { useState } from 'react';
import { ghs, toPesewas } from '../lib/money.js';
import { Button, Chip, Banner } from '../components/ui.jsx';
import { Divider } from './SalesPoint.jsx';

const METHODS = [
  ['cash', 'Cash', 'Change calculated'],
  ['momo', 'MoMo', 'Network and ref'],
  ['card', 'Card', 'Slip number'],
  ['bank', 'Bank', 'Transfer ref'],
  ['credit', 'Credit', 'Customer owes']
];
const NETWORKS = ['MTN', 'Telecel', 'AirtelTigo'];

export function Payment({ total, staff, customer, onBack, onComplete, error }) {
  const [taken, setTaken] = useState([]);
  const [method, setMethod] = useState('cash');
  const [entry, setEntry] = useState('');
  const [network, setNetwork] = useState('MTN');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  const paid = taken.reduce((a, p) => a + p.amount, 0);

  /**
   * The till collects the service total and nothing else.
   *
   * Tips are not handled here on purpose. In Ghana a tip is not expected and not asked for:
   * if a client gives one it goes into the stylist's hand, the salon never holds the money
   * and never owes it onward. Putting a tip prompt in the payment flow would mean asking
   * every customer how much they intend to give before their sale can be closed, which is
   * an American habit and a rude one to import.
   */
  const remaining = Math.max(0, total - paid);
  const change = Math.max(0, paid - total);
  const settled = paid >= total;
  const creditNeedsCustomer = method === 'credit' && !customer;

  const amount = entry === '' ? remaining : toPesewas(entry);

  function add() {
    if (amount <= 0) return;
    const tendered = method === 'cash' ? toPesewas(entry || String(remaining / 100)) : null;
    // Cash over the amount due is change, not extra revenue, so only the due part counts.
    const applied = method === 'cash' ? Math.min(amount, remaining) : amount;
    setTaken(t => [...t, {
      method, amount: applied, network: method === 'momo' ? network : null,
      reference: reference || null, tendered,
      change: method === 'cash' ? Math.max(0, amount - remaining) : 0
    }]);
    setEntry(''); setReference('');
  }

  async function finish() {
    setBusy(true);
    await onComplete(
      taken.map(({ method, amount, network, reference, tendered }) => ({ method, amount, network, reference, tendered }))
    );
    setBusy(false);
  }

  const cashChange = taken.reduce((a, p) => a + (p.change || 0), 0);

  return (
    <div style={{ display: 'flex', height: '100%', margin: -24 }}>
      {/* what is owed, what has been taken, what is left */}
      <div style={{
        width: 480, flex: 'none', background: 'var(--color-bg-surface)',
        borderRight: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column'
      }}>
        <div style={{ padding: '24px 24px 20px' }}>
          <div className="t-label-xs muted">AMOUNT DUE</div>
          <div className="money t-money-display">{ghs(total)}</div>
          {customer && <Chip tone="accent" size="s">{customer.name}</Chip>}
        </div>
        <Divider />
        <div style={{ padding: '16px 24px 8px' }} className="t-label-xs muted">PAYMENTS TAKEN</div>

        {!taken.length && (
          <div style={{ padding: '4px 24px 18px' }}>
            <div className="t-body-l muted">Nothing taken yet</div>
            <div className="t-body-s muted">Choose a method on the right and enter an amount.</div>
          </div>
        )}
        {taken.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 24px' }}>
            <div style={{ flex: 1 }}>
              <div className="t-label-l">{p.method === 'momo' ? `MoMo · ${p.network}` : cap(p.method)}</div>
              <div className="t-body-s muted">
                {p.method === 'cash'
                  ? `Tendered ${ghs(p.tendered)}${p.change ? `, change ${ghs(p.change)}` : ''}`
                  : p.reference ? `Ref ${p.reference}` : 'No reference'}
              </div>
            </div>
            <span className="money t-money-l">{ghs(p.amount)}</span>
            <button className="btn btn-ghost btn-m" style={{ color: 'var(--color-status-danger)' }}
              onClick={() => setTaken(t => t.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}

        <div style={{ flex: 1 }} />

        {error && <div style={{ padding: '0 24px 12px' }}><Banner tone="danger">{error}</Banner></div>}

        <Banner2 settled={settled} remaining={remaining} change={cashChange || change} />

        <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px' }}>
          <Button variant="secondary" onClick={onBack}>Back to cart</Button>
          <Button style={{ flex: 1 }} disabled={!settled || busy} onClick={finish}>
            {busy ? 'Saving' : 'Complete sale'}
          </Button>
        </div>
        {!settled && (
          <div className="t-body-s muted" style={{ padding: '0 24px 20px', marginTop: -12 }}>
            Complete sale stays off until the balance is cleared, or credit is taken deliberately.
            It never quietly creates a debtor.
          </div>
        )}
      </div>

      {/* method, amount, keypad */}
      <div style={{ flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
        <div className="t-label-xs muted">PAYMENT METHOD</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {METHODS.map(([id, name, hint]) => {
            const on = method === id;
            return (
              <button key={id} onClick={() => setMethod(id)}
                style={{
                  flex: 1, minHeight: 84, padding: 14, textAlign: 'left', cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  border: `${on ? 2 : 1}px solid ${on ? 'var(--color-accent-default)' : 'var(--color-border-default)'}`,
                  background: on ? 'var(--color-accent-subtle)' : 'var(--color-bg-surface)',
                  color: on ? 'var(--color-accent-strong)' : 'var(--color-text-primary)', font: 'inherit'
                }}>
                <div className="t-heading-s">{name}</div>
                <div className="t-body-s muted">{hint}</div>
              </button>
            );
          })}
        </div>

        {method === 'momo' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {NETWORKS.map(n => (
              <button key={n} onClick={() => setNetwork(n)} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
                <Chip tone={network === n ? 'accent' : 'neutral'} size="m" showDot={network === n}>{n}</Chip>
              </button>
            ))}
            <input value={reference} onChange={e => setReference(e.target.value)} placeholder="Transaction reference"
              style={{ flex: 1, height: 44, padding: '0 14px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)', font: '400 15px var(--font-ui)' }} />
          </div>
        )}

        {creditNeedsCustomer && (
          <Banner tone="danger" title="Credit needs a named customer">
            You cannot let a walk-in leave owing money. Go back to the cart and attach a customer first.
          </Banner>
        )}

        <div style={{ display: 'flex', gap: 24, flex: 1, minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="t-label-xs muted">{method === 'cash' ? 'CASH TENDERED' : 'AMOUNT'}</div>
            <div style={{
              padding: '16px 20px', border: '2px solid var(--color-accent-default)',
              borderRadius: 'var(--radius-md)', background: 'var(--color-bg-surface)'
            }}>
              <span className="money t-money-xl">{ghs(amount)}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="secondary" size="m" onClick={() => setEntry(String(remaining / 100))}>
                Exact {ghs(remaining, { symbol: false })}
              </Button>
              {[5000, 10000, 20000].map(v => (
                <Button key={v} variant="ghost" size="m" style={{ border: '1px solid var(--color-border-default)' }}
                  onClick={() => setEntry(String(v / 100))}>{v / 100}</Button>
              ))}
            </div>

            <div style={{ flex: 1 }} />
            <Button disabled={amount <= 0 || creditNeedsCustomer} onClick={add}>Add payment</Button>
          </div>

          <Keypad onKey={k => {
            if (k === 'DEL') setEntry(e => e.slice(0, -1));
            else setEntry(e => (e === '' ? k : e + k));
          }} />
        </div>
      </div>
    </div>
  );
}

function Banner2({ settled, remaining, change }) {
  if (!settled) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', padding: '20px 24px', background: 'var(--color-status-warning-bg)' }}>
        <span className="t-label-xs" style={{ color: 'var(--color-status-warning)', flex: 1 }}>REMAINING</span>
        <span className="money t-money-xl" style={{ color: 'var(--color-status-warning)' }}>{ghs(remaining)}</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '20px 24px', background: 'var(--color-status-success-bg)' }}>
      <span className="t-label-xs" style={{ color: 'var(--color-status-success)', flex: 1 }}>
        {change > 0 ? 'CHANGE DUE' : 'SETTLED'}
      </span>
      <span className="money t-money-xl" style={{ color: 'var(--color-status-success)' }}>{ghs(change)}</span>
    </div>
  );
}

function Keypad({ onKey }) {
  const rows = [['7', '8', '9'], ['4', '5', '6'], ['1', '2', '3'], ['00', '0', 'DEL']];
  return (
    <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 8 }}>
          {r.map(k => (
            <button key={k} onClick={() => onKey(k)}
              style={{
                width: 104, height: 64, borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border-default)',
                background: k === 'DEL' ? 'var(--color-bg-subtle)' : 'var(--color-bg-surface)',
                color: k === 'DEL' ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
                cursor: 'pointer',
                font: k === 'DEL' ? '500 15px/20px var(--font-ui)' : '500 24px/32px var(--font-money)'
              }}>{k}</button>
          ))}
        </div>
      ))}
    </div>
  );
}

const cap = s => s.charAt(0).toUpperCase() + s.slice(1);
